/**
 * The two waits a scene makes for its voice, and the bounds that end them — CCT-310.
 *
 * ⚠️ A MODULE OF ITS OWN, WITH NO IMPORTS, SO THE BOUNDS CAN BE RUN IN A TEST. Pinning a bound by
 * searching the source for the tokens of its formula proves nothing: the first `delaiVeille`
 * guard did exactly that and stayed green with the timer no longer armed. These functions take
 * their reader and their answer as arguments, so a test hands them a request that never returns
 * and watches the bound fire. Pulling them out of the hook is what makes that possible — a hook
 * drags React and the API client in with it.
 */

/** Polling cadence. Slow enough not to hammer, short enough not to be noticed. */
export const INTERVALLE_MS = 1500;
/**
 * ⚠️ HARD BOUND ON THE WHOLE-SCREEN WAIT. Past this delay the scene plays, silent: we lose the
 * voice, not the mission.
 */
export const ATTENTE_MAX_MS = 20000;
/**
 * ⚠️ HARD BOUND ON ONE BEAT'S WAIT, and it must be far shorter than the one above: this one
 * freezes a line the player is looking at, with no spinner to explain it.
 *
 * Not a measurement, a decision: giving up here does not waste the audio. The server stores what
 * it synthesised, so replaying the line finds it ready.
 */
export const ATTENTE_LIGNE_MAX_MS = 8000;

/**
 * Polls until the run's voice is ready, and answers `true` at the latest after `maxMs`.
 *
 * ⚠️ THE BOUND IS A TIMER, NOT A COMPARISON MADE AT THE END OF A PROBE.
 *
 * First version compared `Date.now() - start > maxMs` AFTER the probe resolved. MEASURED:
 * `fetchWithTimeout` allows 30 s per call AND retries a GET once (`DEFAULT_TIMEOUT_MS`), so one
 * probe can take ~61 s — and a real first request on dev took 29 247 ms, a cold replica plus a
 * cold connection. The screen held its "preparing voices" spinner for three times the bound it
 * documents, because nothing raced the request. A deadline read only when the slow thing returns
 * bounds nothing.
 *
 * @returns the canceller, to be called on unmount.
 */
export function attendreVoixPrete(
  lire: () => Promise<{ pret: boolean } | null>,
  repondre: (pret: boolean) => void,
  maxMs: number = ATTENTE_MAX_MS,
  intervalleMs: number = INTERVALLE_MS,
): () => void {
  let vivant = true;
  let rendu = false;
  let prochain: ReturnType<typeof setTimeout> | null = null;

  const butoir = setTimeout(() => {
    if (!vivant || rendu) return;
    rendu = true;
    repondre(true);
  }, maxMs);

  const sonder = async () => {
    const etat = await lire();
    if (!vivant || rendu) return;
    // ⚠️ WE READ THE SERVER'S VERDICT, WE DO NOT RECOMPUTE IT. Deducing "ready" from
    // `faits === total` here would miss the render stopped by the cap — and the scene would wait
    // for something that will never progress. Two definitions of one fact end up diverging.
    if (etat && etat.pret) {
      rendu = true;
      clearTimeout(butoir);
      repondre(true);
      return;
    }
    prochain = setTimeout(sonder, intervalleMs);
  };
  void sonder();

  return () => {
    vivant = false;
    clearTimeout(butoir);
    if (prochain) clearTimeout(prochain);
  };
}

/**
 * Asks for one line's audio key, and answers `''` at the latest after `maxMs`.
 *
 * ⚠️ THIS WAIT HAD NO BOUND AT ALL, and it is the one the player sees: the beat shows nothing
 * while the key is `undefined`. The request behind it can take ~61 s on the client, and the
 * server synthesises with a 120 s read timeout of its own.
 *
 * ⚠️ ONLY THE FIRST ANSWER COUNTS. Without that rule a late response would hand a key to a beat
 * that has already moved on, starting a voice over the following line.
 *
 * @returns the canceller, to be called on unmount.
 */
export function demanderCleLigne(
  lire: () => Promise<{ key: string } | null>,
  repondre: (cle: string) => void,
  maxMs: number = ATTENTE_LIGNE_MAX_MS,
): () => void {
  let vivant = true;
  let rendu = false;

  const butoir = setTimeout(() => {
    if (!vivant || rendu) return;
    rendu = true;
    repondre('');
  }, maxMs);

  void lire().then((r) => {
    if (!vivant || rendu) return;
    rendu = true;
    clearTimeout(butoir);
    // `null` = an EXPECTED refusal (no engine, line above the playback cap). We translate it to
    // "no sound" and above all not to an error: the scene must carry on.
    repondre((r && r.key) || '');
  });

  return () => { vivant = false; clearTimeout(butoir); };
}
